import type { UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type IWorkflowRepository from '@repositories/workflowRepository/IWorkflowRepository';
import type {
  WorkflowCreateDTO,
  WorkflowDTO,
  WorkflowExecCreateDTO,
  WorkflowExecDTO,
  WorkflowExecFilter,
  WorkflowsFilter,
} from '@repositories/workflowRepository/workflow.types';
import type { Page } from '@type/Page';
import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import { WorkflowExecModel } from '@repositories/workflowRepository/models/WorkflowExecModel';
import { WorkflowModel } from '@repositories/workflowRepository/models/WorkflowModel';

const workflowRepo = repository.get<IWorkflowRepository>(KEYS.WORKFLOW_REPOSITORY);

export const workflowQueryKey = {
  all: ['workflow'] as const,
  lists: () => [workflowQueryKey.all, 'list'] as const,
  list: (filter?: WorkflowsFilter) => [...workflowQueryKey.lists(), filter] as const,
  details: () => [...workflowQueryKey.all, 'detail'] as const,
  detail: (workflowId: string) => [...workflowQueryKey.details(), workflowId] as const,
  listInfiniteExecutions: (workflowId: string, filter?: WorkflowExecFilter) =>
    [...workflowQueryKey.details(), workflowId, filter] as const,
  detailExecution: (workflowId: string, workflowExecId: string) =>
    [...workflowQueryKey.details(), workflowId, workflowExecId] as const,
};

export const useGetWorkflows = (
  queries?: WorkflowsFilter,
  options?: Omit<UseQueryOptions<Page<WorkflowDTO>, AxiosError, Page<WorkflowModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectWorkflows = useCallback(
    (data: Page<WorkflowDTO>) => ({
      ...data,
      content: data.content?.map(dto => new WorkflowModel(dto)),
    }),
    [],
  );

  return useQuery<Page<WorkflowDTO>, AxiosError, Page<WorkflowModel>>({
    queryKey: workflowQueryKey.list(queries),
    queryFn: () => workflowRepo.getList(queries),
    select: selectWorkflows,
    ...options,
  });
};

export const useGetWorkflow = (
  workflowId: string,
  options?: Omit<UseQueryOptions<WorkflowDTO, AxiosError, WorkflowModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectWorkflow = useCallback((data: WorkflowDTO) => new WorkflowModel(data), []);

  return useQuery<WorkflowDTO, AxiosError, WorkflowModel>({
    queryKey: workflowQueryKey.detail(workflowId),
    queryFn: () => workflowRepo.getOne(workflowId),
    select: selectWorkflow,
    ...options,
  });
};

export const useAddWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rqData: WorkflowCreateDTO) => {
      const workflow = await workflowRepo.create(rqData);
      return new WorkflowModel(workflow);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: workflowQueryKey.lists() });
      queryClient.invalidateQueries({
        queryKey: workflowQueryKey.detail(data.id),
      });
    },
  });
};

export const useDeleteWorkflow = (workflowId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await workflowRepo.delete(workflowId);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowQueryKey.lists() });
      queryClient.invalidateQueries({
        queryKey: workflowQueryKey.detail(workflowId),
      });
    },
  });
};

export const useUpdateWorkflow = (workflowId: string, name?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [workflowId, name],
    mutationFn: async (rqData: WorkflowCreateDTO) => {
      const workflow = await workflowRepo.update(workflowId, rqData);
      return new WorkflowModel(workflow);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowQueryKey.lists() });
      queryClient.invalidateQueries({
        queryKey: workflowQueryKey.detail(workflowId),
      });
    },
    cacheTime: 500,
  });
};

export const useActiveWorkflow = (workflowId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const workflow = await workflowRepo.active(workflowId);
      return new WorkflowModel(workflow);
    },
    onSuccess: () => {
      /**
       * 워크플로우 상세에서 switch toggle 시 목록 리스트 API (useGetWorkflows)가 inactive 상태이기 때문에 (staleTime이 유효) 리스트 목록에서 바로 반영되지 않음.
       * all 로 refetch 처리하여 목록 리스트에서 바로 반영되도록 처리함.
       */
      queryClient.invalidateQueries({
        queryKey: workflowQueryKey.lists(),
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: workflowQueryKey.detail(workflowId),
      });
    },
  });
};

export const useDeactiveWorkflow = (workflowId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const workflow = await workflowRepo.deActive(workflowId);
      return new WorkflowModel(workflow);
    },
    onSuccess: () => {
      /**
       * 워크플로우 상세에서 switch toggle 시 목록 리스트 API (useGetWorkflows)가 inactive 상태이기 때문에 (staleTime이 유효) 리스트 목록에서 바로 반영되지 않음.
       * all 로 refetch 처리하여 목록 리스트에서 바로 반영되도록 처리함.
       */
      queryClient.invalidateQueries({
        queryKey: workflowQueryKey.lists(),
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: workflowQueryKey.detail(workflowId),
      });
    },
  });
};

export const useExecuteWorkflow = (workflowId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rqData: WorkflowExecCreateDTO) => {
      const workflow = await workflowRepo.execute(workflowId, rqData);
      return new WorkflowExecModel(workflow);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowQueryKey.lists() });
      queryClient.invalidateQueries({
        queryKey: workflowQueryKey.detail(workflowId),
      });
    },
  });
};

export const useGetWorkflowExecutionsInfinite = (
  workflowId: string,
  queries?: Omit<WorkflowExecFilter, 'page'>,
  options?: UseInfiniteQueryOptions<Page<WorkflowExecModel>>,
) => {
  return useInfiniteQuery({
    queryKey: workflowQueryKey.listInfiniteExecutions(workflowId, queries),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await workflowRepo.getListExecution(workflowId, {
        ...queries,
        page: pageParam,
      });
      return {
        ...result,
        content: result.content?.map(dto => new WorkflowExecModel(dto)),
      };
    },
    cacheTime: 100 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
    ...options,
    getNextPageParam: lastPage => (lastPage.last ? undefined : lastPage.number + 1),
  });
};

export const useGetWorkflowExecution = (
  execInfo: { workflowId: string; workflowExecId: string },
  options?: Omit<UseQueryOptions<WorkflowExecDTO, AxiosError, WorkflowExecModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const { workflowId, workflowExecId } = execInfo;
  const selectWorkflowExecution = useCallback((data: WorkflowExecDTO) => new WorkflowExecModel(data), []);

  return useQuery<WorkflowExecDTO, AxiosError, WorkflowExecModel>({
    queryKey: workflowQueryKey.detailExecution(workflowId, workflowExecId),
    queryFn: () => workflowRepo.getOneExecution(workflowId, workflowExecId),
    select: selectWorkflowExecution,
    ...options,
  });
};
